import { PageHeader, FilterBar } from "@/components/ui/page";
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
	Plus,
	Search,
	Package,
	Trash2,
	Edit,
	AlertTriangle,
	Pencil,
	Truck,
	Check,
	LayoutGrid,
	List,
	SlidersHorizontal,
} from "lucide-react";
import { productsAPI, categoriesAPI, branchesAPI, stockTransfersAPI, getApiErrorPayload } from "@/lib/api";
import { Product, ProductStatus, ProductType } from "@/types";
import { useTranslation } from "react-i18next";
import { PermissionGate } from "@/features/auth/components/PermissionGate";
import { usePermission } from "@/hooks/usePermission";
import { useAuth } from "@/features/auth/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { getBestBadgeVariant, cn } from "@/lib/utils";
import { Price } from "@/components/ui/price";
import { useCurrency } from "@/hooks/useCurrency";
import { getDashboardProductImageSrc } from "@/lib/productImageUrl";
import { Loading } from "@/components/ui/loader";
import { Pagination } from "@/components/ui/pagination";
import { confirmAction } from "@/components/ui/confirm-dialog";

interface CategoryItem {
	id: number | string;
	name: string;
	nameEn?: string;
	description?: string;
	descriptionEn?: string;
	status: "active" | "inactive";
	products?: { id: number }[];
}

interface CategoryFormData {
	name: string;
	nameEn?: string;
	description?: string;
	descriptionEn?: string;
	status: "active" | "inactive";
}

interface BranchItem {
	id: number;
	name: string;
}

const emptyCategoryForm: CategoryFormData = {
	name: "",
	nameEn: "",
	description: "",
	descriptionEn: "",
	status: "active",
};

const ProductsPage: React.FC = () => {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { toast } = useToast();
	const currency = useCurrency();
	const [searchParams] = useSearchParams();

	const [showFilters, setShowFilters] = useState(false);
	const [view, setView] = useState<"cards" | "table">("cards");
	const [products, setProducts] = useState<Product[]>([]);
	const [categories, setCategories] = useState<CategoryItem[]>([]);
	const [categoryError, setCategoryError] = useState(false);
	const latestRequest = useRef(0);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	// Pagination and filters
	const [currentPage, setCurrentPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [totalProducts, setTotalProducts] = useState(0);
	const [searchTerm, setSearchTerm] = useState(() => searchParams.get("q") ?? "");
	const [selectedCategory, setSelectedCategory] = useState<string>("");
	const [selectedStatus, setSelectedStatus] = useState<ProductStatus | "">("");
	const [selectedType, setSelectedType] = useState<ProductType | "">("");
	const [itemsPerPage, setItemsPerPage] = useState(10);

	// Cross-branch search (checkbox on the search box): off = the user's own branch,
	// on = all branches, with a branch column to tell results apart.
	const { user } = useAuth();
	const canCrossBranch = usePermission("products.readCrossBranch");
	const canReadCategories = usePermission("categories.read");
	const [crossBranchMode, setCrossBranchMode] = useState(() => searchParams.get("crossBranch") === "1");

	// Category management (merged from the categories page)
	const canManageCategories = usePermission(["categories.create", "categories.update", "categories.delete"]);
	const [categoryDialogMode, setCategoryDialogMode] = useState<"create" | "edit" | null>(null);
	const [editingCategory, setEditingCategory] = useState<CategoryItem | null>(null);
	const [categoryForm, setCategoryForm] = useState<CategoryFormData>(emptyCategoryForm);
	const [categorySaving, setCategorySaving] = useState(false);

	// Per-row stock transfer
	const canTransfer = usePermission("stockTransfers.create");
	const [branches, setBranches] = useState<BranchItem[]>([]);
	const [transferProduct, setTransferProduct] = useState<Product | null>(null);
	const [transferToBranchId, setTransferToBranchId] = useState<number | "">("");
	const [transferQty, setTransferQty] = useState(1);
	const [transferNotes, setTransferNotes] = useState("");
	const [transferSubmitting, setTransferSubmitting] = useState(false);

	const hasActiveFilters = Boolean(searchTerm || selectedCategory || selectedStatus || selectedType);
	const clearFilters = () => {
		setSearchTerm("");
		setSelectedCategory("");
		setSelectedStatus("");
		setSelectedType("");
		setCurrentPage(1);
	};

	useEffect(() => {
		fetchProducts();
		// eslint-disable-next-line react-hooks/exhaustive-deps -- fetch when filters change
	}, [currentPage, itemsPerPage, searchTerm, selectedCategory, selectedStatus, selectedType, crossBranchMode]);

	useEffect(() => {
		fetchCategories();
		// eslint-disable-next-line react-hooks/exhaustive-deps -- reload when catalog access changes
	}, [canReadCategories]);

	const fetchProducts = async () => {
		const request = ++latestRequest.current;
		try {
			setIsLoading(true);
			setError(null);

			const response = await productsAPI.getAll({
				page: currentPage,
				limit: itemsPerPage,
				search: searchTerm || undefined,
				categoryId: selectedCategory || undefined,
				status: selectedStatus || undefined,
				type: selectedType || undefined,
				// Scope to the user's branch unless cross-branch search is on
				branchId: !crossBranchMode && user?.branchId ? user.branchId : undefined,
			});

			if (request !== latestRequest.current) return;
			setProducts(response.products);
			setTotalProducts(response.total);
			setTotalPages(Math.ceil(response.total / itemsPerPage));
			// Initialize itemsPerPage from backend response if available
			if (response.limit && response.limit !== itemsPerPage) {
				setItemsPerPage(response.limit);
			}
		} catch (err) {
			if (request !== latestRequest.current) return;
			setError(t("products.errors.fetchFailed"));
			console.error("Products fetch error:", err);
		} finally {
			if (request === latestRequest.current) setIsLoading(false);
		}
	};

	const fetchCategories = async () => {
		setCategoryError(false);
		if (!canReadCategories) {
			setCategories([]);
			return;
		}
		try {
			const response = await categoriesAPI.getAll();
			setCategories(Array.isArray(response) ? response : []);
		} catch (err) {
			setCategoryError(true);
			console.error("Categories fetch error:", err);
		}
	};

	const handleDeleteProduct = async (productId: number | string) => {
		if (await confirmAction(t("products.confirmations.delete"), { confirmLabel: t("common.delete") })) {
			try {
				await productsAPI.delete(productId);
				toast({
					title: t("common.success"),
					description: t("products.messages.deleted"),
				});
				fetchProducts();
				fetchCategories();
			} catch (err) {
				toast({
					title: t("common.error"),
					description: t("products.errors.deleteFailed"),
					variant: "destructive",
				});
			}
		}
	};

	const openCreateCategory = () => {
		setEditingCategory(null);
		setCategoryForm(emptyCategoryForm);
		setCategoryDialogMode("create");
	};

	const openEditCategory = (category: CategoryItem) => {
		setEditingCategory(category);
		setCategoryForm({
			name: category.name,
			nameEn: category.nameEn || "",
			description: category.description || "",
			descriptionEn: category.descriptionEn || "",
			status: category.status,
		});
		setCategoryDialogMode("edit");
	};

	const handleSaveCategory = async () => {
		if (!categoryForm.name.trim()) {
			toast({
				title: t("common.error"),
				description: t("categories.errors.nameRequired"),
				variant: "destructive",
			});
			return;
		}
		try {
			setCategorySaving(true);
			if (categoryDialogMode === "edit" && editingCategory) {
				await categoriesAPI.update(editingCategory.id, categoryForm);
				toast({ title: t("common.success"), description: t("categories.messages.updated") });
			} else {
				await categoriesAPI.create(categoryForm);
				toast({ title: t("common.success"), description: t("categories.messages.created") });
			}
			setCategoryDialogMode(null);
			fetchCategories();
		} catch (error) {
			const payload = getApiErrorPayload(error);
			const errorMessage = t(payload.key, (payload.params ?? {}) as Record<string, string>);
			toast({
				title: t("common.error"),
				description:
					errorMessage === payload.key
						? t(
								categoryDialogMode === "edit"
									? "categories.errors.updateFailed"
									: "categories.errors.createFailed",
							)
						: errorMessage,
				variant: "destructive",
			});
		} finally {
			setCategorySaving(false);
		}
	};

	const handleDeleteCategory = async (category: CategoryItem) => {
		if (!(await confirmAction(t("categories.confirmations.delete"), { confirmLabel: t("common.delete") }))) return;
		try {
			await categoriesAPI.delete(category.id);
			toast({ title: t("common.success"), description: t("categories.messages.deleted") });
			if (String(selectedCategory) === String(category.id)) {
				setSelectedCategory("");
				setCurrentPage(1);
			}
			fetchCategories();
		} catch (error) {
			toast({
				title: t("common.error"),
				description: t("categories.errors.deleteFailed"),
				variant: "destructive",
			});
		}
	};

	const selectCategoryFilter = (categoryId: string) => {
		setSelectedCategory(prev => (prev === categoryId ? "" : categoryId));
		setCurrentPage(1);
	};

	const openTransferDialog = async (product: Product) => {
		setTransferProduct(product);
		setTransferToBranchId("");
		setTransferQty(1);
		setTransferNotes("");
		if (branches.length === 0) {
			try {
				const data = await branchesAPI.getAll();
				setBranches(Array.isArray(data) ? data : []);
			} catch (err) {
				toast({
					title: t("common.error"),
					description: t("products.errors.fetchBranchesFailed"),
					variant: "destructive",
				});
			}
		}
	};

	const handleTransferSubmit = async () => {
		const product = transferProduct;
		if (!product?.branchId || !product.id) {
			toast({
				title: t("common.error"),
				description: t("stockTransfers.errors.selectBranches"),
				variant: "destructive",
			});
			return;
		}
		if (!transferToBranchId || transferToBranchId === product.branchId) {
			toast({
				title: t("common.error"),
				description: t("stockTransfers.errors.sameBranch"),
				variant: "destructive",
			});
			return;
		}
		if (transferQty <= 0) {
			toast({
				title: t("common.error"),
				description: t("stockTransfers.errors.addItems"),
				variant: "destructive",
			});
			return;
		}
		if (product.trackQuantity && transferQty > product.quantity) {
			toast({
				title: t("common.error"),
				description: t("products.transfer.exceedsOnHand"),
				variant: "destructive",
			});
			return;
		}
		try {
			setTransferSubmitting(true);
			await stockTransfersAPI.create({
				fromBranchId: product.branchId,
				toBranchId: Number(transferToBranchId),
				items: [{ productId: Number(product.id), quantity: transferQty }],
				notes: transferNotes.trim() || undefined,
			});
			toast({ title: t("common.success"), description: t("stockTransfers.messages.created") });
			setTransferProduct(null);
		} catch (error) {
			const payload = getApiErrorPayload(error);
			const errorMessage = t(payload.key, (payload.params ?? {}) as Record<string, string>);
			toast({
				title: t("common.error"),
				description: errorMessage || t("stockTransfers.errors.createFailed"),
				variant: "destructive",
			});
		} finally {
			setTransferSubmitting(false);
		}
	};

	const getStatusBadge = (status: ProductStatus) => {
		const statusConfig = {
			[ProductStatus.ACTIVE]: {
				variant: "success",
				text: t("products.filters.active"),
			},
			[ProductStatus.INACTIVE]: {
				variant: "warning",
				text: t("products.filters.inactive"),
			},
			[ProductStatus.OUT_OF_STOCK]: {
				variant: "danger",
				text: t("products.filters.outOfStock"),
			},
			[ProductStatus.DISCONTINUED]: {
				variant: "outline",
				text: t("products.filters.discontinued"),
			},
		};

		const config = statusConfig[status];
		return <Badge variant={getBestBadgeVariant(config.variant)}>{config.text}</Badge>;
	};

	const getTypeBadge = (type: ProductType) => {
		const typeConfig = {
			[ProductType.PRODUCT]: {
				variant: "info",
				text: t("products.types.product"),
			},
			[ProductType.SERVICE]: {
				variant: "violet",
				text: t("products.types.service"),
			},
			[ProductType.MENU_ITEM]: {
				variant: "orange",
				text: t("products.types.menuItem"),
			},
			[ProductType.COMBO]: {
				variant: "violet",
				text: t("products.types.combo"),
			},
		};

		const config = typeConfig[type];
		return <Badge variant={(config?.variant ?? "default") as any}>{config?.text ?? type}</Badge>;
	};

	return (
		<div className="space-y-5 p-4 md:p-6 products-workspace">
			{/* Header: summary + add product */}
			<PageHeader
				title={t("navigation.products")}
				description={
					<>
						{t("referenceUI.productCount", { count: totalProducts })}
						{canReadCategories && <> · {t("referenceUI.categoryCount", { count: categories.length })}</>}
					</>
				}
				actions={
					<>
						<PermissionGate permission={["inventory.create", "products.create"]}>
							<Button
								className="flex items-center gap-2 shrink-0"
								onClick={() => navigate("/products/new")}
							>
								<Plus className="h-4 w-4" />
								{t("products.actions.create")}
							</Button>
						</PermissionGate>
					</>
				}
			/>

			{categoryError && (
				<div
					role="status"
					className="flex flex-wrap gap-2 items-center rounded-lg border border-warning/25 bg-warning/10 p-3 text-sm text-warning"
				>
					<span>{t("workspace.categoriesUnavailable")}</span>
					<Button variant="outline" size="sm" onClick={fetchCategories}>
						{t("common.retry")}
					</Button>
				</div>
			)}
			{/* Categories chips (filter + inline management) */}
			<PermissionGate permission="categories.read">
				<section className="catalog-categories" aria-label={t("categories.title")}>
					<div className="flex flex-wrap items-center justify-between gap-3">
						<h2 className="text-sm font-semibold">{t("referenceUI.categories")}</h2>
						<PermissionGate permission="categories.create">
							<Button variant="outline" size="sm" onClick={openCreateCategory}>
								<Plus className="h-4 w-4 me-1" />
								{t("categories.actions.add")}
							</Button>
						</PermissionGate>
					</div>
					<div className="flex flex-wrap gap-2">
						<button
							type="button"
							onClick={() => {
								setSelectedCategory("");
								setCurrentPage(1);
							}}
							aria-pressed={selectedCategory === ""}
							className={cn(
								"shrink-0 flex items-center gap-2 rounded-lg min-h-11 border px-4 py-2 text-sm transition-colors",
								selectedCategory === ""
									? "bg-primary/10 text-primary border-transparent font-semibold"
									: "bg-card text-muted-foreground hover:border-primary/50",
							)}
						>
							{t("common.all")}
						</button>
						{categories.map(category => {
							const isSelected = String(selectedCategory) === String(category.id);
							return (
								<div
									key={category.id}
									className={cn(
										"shrink-0 flex items-center gap-1.5 rounded-lg border ps-4 pe-2 py-0 text-sm transition-colors",
										isSelected
											? "border-primary bg-secondary text-primary font-semibold"
											: "bg-card text-muted-foreground hover:border-primary/50",
										category.status === "inactive" && "opacity-60",
									)}
								>
									<button
										type="button"
										className="flex min-h-11 items-center gap-2"
										aria-pressed={isSelected}
										onClick={() => selectCategoryFilter(String(category.id))}
									>
										<span>{category.name}</span>
										<span
											className={cn(
												"rounded-full px-2 py-0.5 text-xs",
												isSelected
													? "bg-primary/10 text-primary"
													: "bg-muted text-muted-foreground",
											)}
										>
											{category.products?.length ?? 0}
										</span>
									</button>
									{canManageCategories && isSelected && (
										<span className="flex items-center">
											<PermissionGate permission="categories.update">
												<button
													type="button"
													className="rounded-full min-h-11 min-w-11 flex items-center justify-center hover:bg-muted"
													onClick={() => openEditCategory(category)}
													title={t("common.edit")}
													aria-label={t("common.edit")}
												>
													<Pencil className="h-3 w-3" />
												</button>
											</PermissionGate>
											<PermissionGate permission="categories.delete">
												<button
													type="button"
													className="rounded-full min-h-11 min-w-11 flex items-center justify-center hover:bg-destructive/10 hover:text-destructive"
													onClick={() => handleDeleteCategory(category)}
													title={t("common.delete")}
													aria-label={t("common.delete")}
												>
													<Trash2 className="h-3 w-3" />
												</button>
											</PermissionGate>
										</span>
									)}
								</div>
							);
						})}
					</div>
				</section>
			</PermissionGate>

			{/* Search + cross-branch checkbox + status filter */}
			<FilterBar className="flex flex-wrap gap-3 items-center">
				<div className="relative flex-1 min-w-[min(100%,240px)]">
					<Search className="absolute start-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
					<Input
						aria-label={t("products.filters.searchPlaceholder")}
						placeholder={
							crossBranchMode
								? t("products.crossBranch.queryPlaceholder")
								: t("products.filters.searchPlaceholder")
						}
						value={searchTerm}
						onChange={e => {
							setSearchTerm(e.target.value);
							setCurrentPage(1);
						}}
						className="ps-10"
					/>
				</div>
				<Button
					variant="outline"
					aria-expanded={showFilters}
					aria-controls="product-filters"
					onClick={() => setShowFilters(v => !v)}
				>
					<SlidersHorizontal className="me-2 h-4 w-4" aria-hidden />
					{t("common.filter")}
					{(selectedStatus || selectedType || crossBranchMode) && (
						<span className="ms-1 rounded-full bg-secondary px-2">
							{[selectedStatus, selectedType, crossBranchMode].filter(Boolean).length}
						</span>
					)}
				</Button>
				<div className="flex justify-end gap-1" role="group" aria-label={t("products.title")}>
					{(["cards", "table"] as const).map(mode => (
						<Button
							key={mode}
							variant={view === mode ? "secondary" : "ghost"}
							size="sm"
							aria-pressed={view === mode}
							onClick={() => setView(mode)}
						>
							{mode === "cards" ? (
								<LayoutGrid className="me-2 h-4 w-4" aria-hidden />
							) : (
								<List className="me-2 h-4 w-4" aria-hidden />
							)}
							{t(mode === "cards" ? "workspace.cards" : "workspace.list")}
						</Button>
					))}
				</div>
			</FilterBar>
			<div id="product-filters" hidden={!showFilters}>
				<div className="flex flex-wrap items-center gap-3 p-3 rounded-lg border bg-card">
					{canCrossBranch && (
						<button
							type="button"
							role="checkbox"
							aria-checked={crossBranchMode}
							onClick={() => {
								setCrossBranchMode(prev => !prev);
								setCurrentPage(1);
							}}
							className="flex items-center gap-2 rounded-md border bg-card px-3 py-3 text-sm cursor-pointer select-none shrink-0"
						>
							<span
								className={cn(
									"flex h-4 w-4 items-center justify-center rounded-sm border",
									crossBranchMode ? "border-green-500 bg-success/10" : "border-primary",
								)}
							>
								{crossBranchMode && <Check className="h-3.5 w-3.5" />}
							</span>
							<span className={crossBranchMode ? "font-semibold" : "text-muted-foreground"}>
								{t("navigation.crossBranchSearch")}
							</span>
						</button>
					)}
					<select
						aria-label={t("products.table.status")}
						value={selectedStatus}
						onChange={e => {
							setSelectedStatus(e.target.value as ProductStatus | "");
							setCurrentPage(1);
						}}
						className="flex h-12 w-full sm:w-48 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
					>
						<option value="">{t("products.filters.allStatuses")}</option>
						<option value={ProductStatus.ACTIVE}>{t("products.filters.active")}</option>
						<option value={ProductStatus.INACTIVE}>{t("products.filters.inactive")}</option>
						<option value={ProductStatus.OUT_OF_STOCK}>{t("products.filters.outOfStock")}</option>
						<option value={ProductStatus.DISCONTINUED}>{t("products.filters.discontinued")}</option>
					</select>
					<select
						aria-label={t("products.table.type")}
						value={selectedType}
						onChange={e => {
							setSelectedType(e.target.value as ProductType | "");
							setCurrentPage(1);
						}}
						className="flex h-12 w-full sm:w-48 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
					>
						<option value="">{t("products.filters.allTypes")}</option>
						<option value={ProductType.PRODUCT}>{t("products.types.product")}</option>
						<option value={ProductType.SERVICE}>{t("products.types.service")}</option>
						<option value={ProductType.MENU_ITEM}>{t("products.types.menuItem")}</option>
						<option value={ProductType.COMBO}>{t("products.types.combo")}</option>
					</select>
					{hasActiveFilters && (
						<Button variant="link" className="text-destructive" onClick={clearFilters}>
							{t("products.filters.clearFilters")}
						</Button>
					)}
				</div>
			</div>
			{/* Products Table */}
			<Card className={view === "cards" ? "border-0 bg-transparent shadow-none" : ""}>
				<CardContent className={view === "cards" ? "p-0" : "p-3"}>
					{isLoading ? (
						<Loading />
					) : view === "cards" ? (
						<div className="product-grid">
							{products.map(product => (
								<article key={product.id} className="product-tile flex flex-col gap-3">
									<>
										<div className="product-artwork">
											{product.category?.name && (
												<span className="product-category">{product.category.name}</span>
											)}
											{product.image ? (
												<img
													src={getDashboardProductImageSrc(product.image)}
													alt=""
													className="h-28 w-full object-contain p-3"
												/>
											) : (
												<span className="product-initial" aria-hidden="true">
													{Array.from(product.name || "")[0] || (
														<Package className="h-7 w-7" />
													)}
												</span>
											)}
										</div>
										<div className="min-w-0 flex-1">
											<h2 className="font-semibold text-base break-words">{product.name}</h2>
										</div>
									</>
									<div className="flex flex-wrap gap-2">
										{getStatusBadge(product.status)}
										{getTypeBadge(product.type)}
									</div>
									<dl className="grid grid-cols-2 gap-2 text-sm">
										<div>
											<dt className="text-xs text-muted-foreground">
												{t("products.table.price")}
											</dt>
											<dd className="font-semibold mt-1 text-lg">
												<Price value={product.sellingPrice} currency={currency} />
											</dd>
										</div>
										<div>
											<dt className="text-xs text-muted-foreground">
												{t("products.table.stock")}
											</dt>
											<dd className="font-semibold mt-1 flex items-center gap-1">
												{product.trackQuantity ? product.quantity : "—"}
												{product.trackQuantity &&
													product.quantity <= (product.lowStockAlert || 0) && (
														<AlertTriangle
															className="h-4 w-4 text-warning"
															aria-label={t("dashboard.charts.lowStock")}
														/>
													)}
											</dd>
										</div>
									</dl>
									{crossBranchMode && (
										<p className="text-xs text-muted-foreground">{product.branch?.name || "—"}</p>
									)}
									<div className="flex flex-wrap gap-2 border-t border-border/60 pt-3 mt-auto">
										<PermissionGate
											permission={[
												"inventory.read",
												"products.read",
												"inventory.update",
												"products.update",
											]}
										>
											<Button
												variant="outline"
												size="sm"
												className="flex-1"
												onClick={() => navigate(`/products/${product.id}`)}
											>
												{t("common.view")}
											</Button>
										</PermissionGate>
										{canTransfer && product.branchId != null && product.trackQuantity && (
											<Button
												variant="outline"
												size="sm"
												onClick={() => openTransferDialog(product)}
												aria-label={t("products.transfer.title")}
												title={t("products.transfer.title")}
											>
												<Truck className="h-4 w-4" />
											</Button>
										)}
										<PermissionGate permission={["inventory.delete", "products.delete"]}>
											<Button
												variant="ghost"
												size="sm"
												onClick={() => handleDeleteProduct(product.id)}
												aria-label={t("common.delete")}
											>
												<Trash2 className="h-4 w-4 text-destructive" />
											</Button>
										</PermissionGate>
									</div>
								</article>
							))}
						</div>
					) : (
						<div className="record-surface">
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead className="w-12">#</TableHead>
										<TableHead>{t("products.table.type")}</TableHead>
										<TableHead>{t("products.table.image")}</TableHead>
										<TableHead>{t("products.table.name")}</TableHead>
										{crossBranchMode && (
											<TableHead className="text-center">
												{t("products.crossBranch.branch")}
											</TableHead>
										)}
										<TableHead>{t("products.table.barcode")}</TableHead>
										<TableHead className="text-center">{t("products.table.category")}</TableHead>
										<TableHead>{t("products.table.price")}</TableHead>
										<TableHead className="text-center">{t("products.table.stock")}</TableHead>
										<TableHead className="text-center">
											{t("products.table.headers.createdBy")}
										</TableHead>
										<TableHead className="text-center">{t("products.table.status")}</TableHead>
										<TableHead className="text-center w-32">{t("common.actions")}</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{products.map(product => (
										<TableRow key={product.id}>
											<TableCell className="font-medium">{product.id}</TableCell>
											<TableCell>{getTypeBadge(product.type)}</TableCell>
											<TableCell>
												<div className="w-12 h-12 rounded-md bg-muted flex items-center justify-center">
													{product.image ? (
														<img
															src={getDashboardProductImageSrc(product.image)}
															alt={product.name}
															className="w-full h-full object-cover rounded-md"
														/>
													) : (
														<Package className="h-6 w-6 text-muted-foreground" />
													)}
												</div>
											</TableCell>
											<TableCell>
												<div className="max-w-48">
													<div className="font-medium line-clamp-2">{product.name}</div>
													{product.description && (
														<div className="text-sm text-muted-foreground line-clamp-1">
															{product.description}
														</div>
													)}
												</div>
											</TableCell>
											{crossBranchMode && (
												<TableCell className="text-center">
													{product.branch?.name ? (
														<Badge variant="outline" className="bg-card">
															{product.branch.name}
														</Badge>
													) : (
														<span className="text-muted-foreground">-</span>
													)}
												</TableCell>
											)}
											<TableCell>
												<bdi className="text-sm">{product.barcode || "—"}</bdi>
											</TableCell>
											<TableCell className="text-center">
												{product.category ? (
													<Badge variant="outline" className="bg-card">
														{product.category.name}
													</Badge>
												) : (
													<span className="text-muted-foreground">-</span>
												)}
											</TableCell>
											<TableCell>
												<div className="text-start">
													<div className="text-xs flex-row items-center">
														<span className="text-muted-foreground">
															{t("products.form.sellingPriceOnly")}:{" "}
														</span>
														<span className="font-semibold text-success">
															<Price value={product.sellingPrice} currency={currency} />
														</span>
													</div>
													{product.purchasePrice &&
														product.purchasePrice !== product.sellingPrice && (
															<div className="text-xs flex-row items-center text-muted-foreground">
																<span className="text-muted-foreground">
																	{t("products.form.purchasePrice")}:{" "}
																</span>
																<span className="text-warning">
																	<Price
																		value={product.purchasePrice}
																		currency={currency}
																	/>
																</span>
															</div>
														)}
												</div>
											</TableCell>
											<TableCell className="text-center">
												{product.trackQuantity ? (
													<div className="flex items-center gap-2">
														<span className="font-medium">{product.quantity}</span>
														{product.quantity <= (product.lowStockAlert || 0) && (
															<AlertTriangle className="h-4 w-4 text-warning" />
														)}
													</div>
												) : (
													<span className="text-muted-foreground">-</span>
												)}
											</TableCell>
											<TableCell className="text-center">
												{product.createdBy ? (
													<span className="text-sm">
														{product.createdBy.firstName} {product.createdBy.lastName}
													</span>
												) : (
													<span className="text-muted-foreground">-</span>
												)}
											</TableCell>
											<TableCell className="text-center">
												{getStatusBadge(product.status)}
											</TableCell>
											<TableCell>
												<div className="flex items-center gap-1">
													<PermissionGate
														permission={[
															"inventory.read",
															"products.read",
															"inventory.update",
															"products.update",
														]}
													>
														<Button
															size="sm"
															variant="outline"
															onClick={() => navigate(`/products/${product.id}`)}
															title={t("common.view")}
															aria-label={t("common.view")}
														>
															<Edit className="h-4 w-4 text-primary" />
														</Button>
													</PermissionGate>
													{canTransfer &&
														product.branchId != null &&
														product.trackQuantity && (
															<Button
																size="sm"
																variant="outline"
																onClick={() => openTransferDialog(product)}
																title={t("products.transfer.title")}
																aria-label={t("products.transfer.title")}
															>
																<Truck className="h-4 w-4 text-success" />
															</Button>
														)}
													<PermissionGate
														permission={["inventory.delete", "products.delete"]}
													>
														<Button
															size="sm"
															variant="outline"
															onClick={() => handleDeleteProduct(product.id)}
															title={t("common.delete")}
															aria-label={t("common.delete")}
														>
															<Trash2 className="h-4 w-4 text-destructive" />
														</Button>
													</PermissionGate>
												</div>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>
					)}

					{/* Empty State */}
					{!isLoading && products.length === 0 && !error && (
						<div className="text-center py-12">
							<Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
							<h3 className="text-lg font-semibold mb-2">
								{hasActiveFilters ? t("common.noResults") : t("products.empty.title")}
							</h3>
							{hasActiveFilters ? (
								<Button variant="outline" onClick={clearFilters}>
									{t("products.filters.clearFilters")}
								</Button>
							) : (
								<>
									<p className="text-muted-foreground mb-4">{t("products.empty.description")}</p>
									<PermissionGate permission={["inventory.create", "products.create"]}>
										<Button onClick={() => navigate("/products/new")}>
											<Plus className="h-4 w-4 mx-2" />
											{t("products.table.headers.addButton")}
										</Button>
									</PermissionGate>
								</>
							)}
						</div>
					)}
				</CardContent>
			</Card>

			{/* Pagination */}
			<Pagination
				currentPage={currentPage}
				totalPages={totalPages}
				totalItems={totalProducts}
				itemsPerPage={itemsPerPage}
				onPageChange={setCurrentPage}
				onPageSizeChange={setItemsPerPage}
				itemName="products.pagination.products"
			/>

			{/* Error State */}
			{error && (
				<Card className="border-destructive">
					<CardContent className="pt-6">
						<div className="text-center" role="alert">
							<AlertTriangle className="h-12 w-12 text-destructive mx-auto mb-4" />
							<h3 className="text-lg font-semibold mb-2">{t("products.errors.loadingError")}</h3>
							<p className="text-muted-foreground mb-4">{error}</p>
							<Button onClick={fetchProducts}>{t("common.tryAgain")}</Button>
						</div>
					</CardContent>
				</Card>
			)}

			{/* Per-row stock transfer dialog */}
			<Dialog open={transferProduct !== null} onOpenChange={open => !open && setTransferProduct(null)}>
				<DialogContent className="max-w-lg">
					<DialogHeader>
						<DialogTitle>{t("products.transfer.title")}</DialogTitle>
						<DialogDescription>{t("products.transfer.description")}</DialogDescription>
					</DialogHeader>
					{transferProduct && (
						<div className="space-y-4 py-2">
							<div className="rounded-md border bg-muted/40 p-3 text-sm space-y-1">
								<div>
									<span className="text-muted-foreground">{t("products.form.name")}: </span>
									<span className="font-medium">{transferProduct.name}</span>
								</div>
								<div>
									<span className="text-muted-foreground">{t("products.form.barcode")}: </span>
									<span>{transferProduct.barcode || "-"}</span>
								</div>
								<div>
									<span className="text-muted-foreground">{t("products.form.branch")}: </span>
									<span>
										{transferProduct.branch?.name ??
											branches.find(b => b.id === transferProduct.branchId)?.name ??
											"-"}
									</span>
								</div>
								<div>
									<span className="text-muted-foreground">{t("products.transfer.onHand")}: </span>
									<span>{transferProduct.quantity}</span>
								</div>
							</div>
							<div className="space-y-2">
								<Label>{t("stockTransfers.toBranch")}</Label>
								<Select
									value={transferToBranchId === "" ? "" : String(transferToBranchId)}
									onValueChange={v => setTransferToBranchId(v ? parseInt(v, 10) : "")}
								>
									<SelectTrigger aria-label={t("stockTransfers.toBranch")}>
										<SelectValue placeholder={t("stockTransfers.selectBranch")} />
									</SelectTrigger>
									<SelectContent>
										{branches
											.filter(b => b.id !== transferProduct.branchId)
											.map(b => (
												<SelectItem key={b.id} value={String(b.id)}>
													{b.name}
												</SelectItem>
											))}
									</SelectContent>
								</Select>
							</div>
							<div className="space-y-2">
								<Label htmlFor="rowTransferQty">{t("stockTransfers.quantity")}</Label>
								<Input
									id="rowTransferQty"
									type="number"
									min={1}
									step={1}
									value={transferQty}
									onChange={e => setTransferQty(Math.max(1, parseInt(e.target.value, 10) || 1))}
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="rowTransferNotes">{t("common.notes")}</Label>
								<Input
									id="rowTransferNotes"
									value={transferNotes}
									onChange={e => setTransferNotes(e.target.value)}
									placeholder={t("common.optional")}
								/>
							</div>
						</div>
					)}
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setTransferProduct(null)}
							disabled={transferSubmitting}
						>
							{t("common.cancel")}
						</Button>
						<Button onClick={handleTransferSubmit} disabled={transferSubmitting}>
							{transferSubmitting ? (
								<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
							) : (
								<span>{t("products.transfer.submit")}</span>
							)}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Create / Edit Category Dialog */}
			<Dialog open={categoryDialogMode !== null} onOpenChange={open => !open && setCategoryDialogMode(null)}>
				<DialogContent className="max-w-2xl">
					<DialogHeader>
						<DialogTitle>
							{categoryDialogMode === "edit"
								? t("categories.dialogs.edit.title")
								: t("categories.dialogs.create.title")}
						</DialogTitle>
						<DialogDescription>
							{categoryDialogMode === "edit"
								? t("categories.dialogs.edit.description")
								: t("categories.dialogs.create.description")}
						</DialogDescription>
					</DialogHeader>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
						<div className="space-y-2">
							<Label htmlFor="category-name">{t("categories.fields.name")} *</Label>
							<Input
								id="category-name"
								value={categoryForm.name}
								onChange={e => setCategoryForm({ ...categoryForm, name: e.target.value })}
								placeholder={t("categories.fields.namePlaceholder")}
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="category-nameEn">{t("categories.fields.nameEn")}</Label>
							<Input
								id="category-nameEn"
								value={categoryForm.nameEn}
								onChange={e => setCategoryForm({ ...categoryForm, nameEn: e.target.value })}
								placeholder={t("categories.fields.nameEnPlaceholder")}
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="category-status">{t("categories.fields.status")}</Label>
							<Select
								value={categoryForm.status}
								onValueChange={(value: "active" | "inactive") =>
									setCategoryForm({ ...categoryForm, status: value })
								}
							>
								<SelectTrigger id="category-status">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="active">{t("common.active")}</SelectItem>
									<SelectItem value="inactive">{t("common.inactive")}</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div className="md:col-span-2 space-y-2">
							<Label htmlFor="category-description">{t("categories.fields.description")}</Label>
							<Input
								id="category-description"
								value={categoryForm.description}
								onChange={e => setCategoryForm({ ...categoryForm, description: e.target.value })}
								placeholder={t("categories.fields.descriptionPlaceholder")}
							/>
						</div>
						<div className="md:col-span-2 space-y-2">
							<Label htmlFor="category-descriptionEn">{t("categories.fields.descriptionEn")}</Label>
							<Input
								id="category-descriptionEn"
								value={categoryForm.descriptionEn}
								onChange={e => setCategoryForm({ ...categoryForm, descriptionEn: e.target.value })}
								placeholder={t("categories.fields.descriptionEnPlaceholder")}
							/>
						</div>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setCategoryDialogMode(null)}>
							{t("common.cancel")}
						</Button>
						<Button onClick={handleSaveCategory} disabled={categorySaving}>
							{categoryDialogMode === "edit" ? t("common.update") : t("common.create")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
};

export default ProductsPage;
